import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { formatNumber } from '@dcp/shared/utils/formatters'
import { useOrganization } from '@/contexts/OrganizationContext'
import { createTenantDb } from '@/lib/db-tenant'
import { invalidateAfterTransactionImportDelete } from '@/lib/file-import-invalidation'

/** `delete_transaction_import` raises this (HTTP 404) when the import is gone or not a completed transaction import. */
const NOT_DELETABLE = 'PT404'

export interface DeleteTransactionImportInput {
  fileImportId: string
  /** Shown in the success toast. */
  fileName: string
}

/**
 * Delete a completed transaction import together with every transaction it
 * inserted. Toasts the outcome itself, so callers only decide what to do with
 * the dialog: the toast must survive the Import History row (and the dialog it
 * owns) unmounting once the refreshed list no longer contains the import.
 */
export function useDeleteTransactionImport() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const orgId = currentOrganization?.id

  return useMutation<number, Error, DeleteTransactionImportInput>({
    mutationFn: async ({ fileImportId }) => {
      if (!orgId) throw new Error('No organization selected')
      const { data, error } = await createTenantDb(orgId).fileImports.deleteTransactionImport(fileImportId)
      if (error) {
        if (error.code === NOT_DELETABLE) {
          // Usually deleted in another tab: the list and everything its
          // transactions fed are out of date here, so refresh as after a delete.
          invalidateAfterTransactionImportDelete(queryClient, orgId, fileImportId)
          throw new Error('This import can no longer be deleted.')
        }
        throw new Error(error.message)
      }
      return data
    },
    // Success only: the RPC deletes the transactions and the import in one
    // transaction, so a refusal or failure changed nothing (PT404 is handled
    // above).
    onSuccess: (deleted, { fileImportId, fileName }) => {
      if (orgId) invalidateAfterTransactionImportDelete(queryClient, orgId, fileImportId)
      toast.success(`Deleted ${fileName} and ${formatNumber(deleted)} transaction${deleted === 1 ? '' : 's'}.`)
    },
    onError: (error) => toast.error(error.message),
  })
}
